import List, { ListItem, ListItemIcon, ListItemText } from "../List";
import Typography from "@/components/Typography";
import styles from "./MobileNotificationPanel.module.scss";
import NotificationItem, { NotificationItemLoader } from "./NotificationItem";
import MobileNotificationItem from "./MobileNotificationItem";
import { MoreIcon, VisibleIcon } from "@/components/Icon";
import { IconButton } from "@/components/Button";
import React, { useEffect, useState } from "react";
import Menu, { MenuItem } from "@/components/Menu";
import {
  useMakeAllNotificationSeenMutation,
  useMakeNotificationSeenMutation,
  useNotificationsQuery,
  useNotificationStatisticsQuery,
} from "@/fetch/notifications";
import compact from "lodash/compact";
import Divider from "../Divider";
import { useInView } from "react-intersection-observer";
import { MobileModal } from "@/components/Modal";
import { MobilePaperTitle } from "@/components/PageTitle";
import Tabs, { Tab } from "@/components/Tabs";
import { NotificationTypes, useNotificationGroup } from "@/hooks";
import cx from "classnames";
import Button from "@/components/Button";

const Loader = () => (
  <>
    {Array(3)
      .fill(null)
      .map((item, index) => (
        <React.Fragment key={index}>
          <NotificationItemLoader />
          <Divider />
        </React.Fragment>
      ))}
  </>
);

const MobileNotificationPanel: React.FC<{
  classes?: { list?: string };
  className?: string;
  onExit?: () => void;
}> = ({ classes, className, onExit }) => {
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const { mutateAsync: makeAllAsSeen, isLoading: isMarkingAllAsSeen } =
    useMakeAllNotificationSeenMutation();
  const { mutateAsync: makeAsSeen } = useMakeNotificationSeenMutation();
  const {
    refetch: refetchNotificationStatistics,
    data: notificationStatistics,
  } = useNotificationStatisticsQuery();

  const unseenNotificationsCount = notificationStatistics?.unseen || 0;

  const { ref: loaderContainerRef, inView: isLoaderInView } = useInView({
    threshold: 0,
  });
  const {
    data: notificationsData,
    isLoading: isNotificationsLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch: refetchNotifications,
  } = useNotificationsQuery();

  const notifications = compact(notificationsData?.pages?.flat(2));
  const { lastSevenDaysNotifications, restNotifications } =
    useNotificationGroup(
      notifications,
      selectedTab === 0
        ? "All"
        : selectedTab === 1
        ? NotificationTypes.OFFICIAL
        : NotificationTypes.ACTIVITY
    );
  const hasNotifications =
    Boolean(notifications?.length > 0) &&
    (lastSevenDaysNotifications.length > 0 || restNotifications.length > 0);
  const tabHasNotifications =
    lastSevenDaysNotifications.length > 0 || restNotifications.length > 0;

  useEffect(() => {
    if (
      !isLoaderInView ||
      !hasNextPage ||
      isNotificationsLoading ||
      isFetchingNextPage
    )
      return;

    fetchNextPage();
  }, [
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoaderInView,
    isNotificationsLoading,
  ]);

  const onSeenAll = async () => {
    try {
      makeAllAsSeen();
    } catch (error) {
      console.error(error);
    }
  };

  const onNotificationItemClick = async (notificationId: string) => {
    onExit?.();
    try {
      await makeAsSeen({ notificationId });
      refetchNotifications();
      refetchNotificationStatistics();
    } catch (error) {
      console.error();
    }
  };

  const notificationTabs = [
    {
      label: "All",
      value: 0,
    },
    {
      label: "Official",
      value: 1,
    },
    {
      label: "Activity",
      value: 2,
    },
  ];

  return (
    <MobileModal>
      <MobilePaperTitle title="Notifications" onGoBack={() => onExit?.()}>
        <Button
          variant="text"
          className={styles.marlAllButton}
          onClick={onSeenAll}
          disabled={isMarkingAllAsSeen || unseenNotificationsCount === 0}
        >
          Mark all as read
        </Button>
      </MobilePaperTitle>
      <Tabs
        className={cx(styles.tabs)}
        value={selectedTab}
        variant="scrollable"
        // scrollButtons="auto"
        TabIndicatorProps={{ style: { display: "none" } }}
        onChange={(e, value) => setSelectedTab(value)}
      >
        {notificationTabs.map(({ label, value }) => (
          <Tab
            key={label}
            aria-selected={value === selectedTab}
            label={label}
            tabIndex={0}
            value={value}
            className={cx(styles.tab, {
              [styles.selectedTab]: value === selectedTab,
            })}
          />
        ))}
      </Tabs>
      <div className={styles.container}>
        <List
          className={cx(
            styles.notificationsListRoot,
            styles.emptyList,
            classes?.list
          )}
        >
          {/* {!hasNotifications && !isNotificationsLoading && (
            <ListItem className={styles.textItem}>
              <Typography color="text.secondary" align="center" variant="body2">
                You have no notifications yet
              </Typography>
            </ListItem>
          )} */}

          {!hasNotifications && isNotificationsLoading && <Loader />}
          {selectedTab == 0 && !hasNotifications && !isNotificationsLoading && (
            <ListItem className={cx(styles.textItem, styles.emptyItem)}>
              <Typography color="text.secondary" align="center" variant="body1">
                Nothing to see here yet.
              </Typography>
            </ListItem>
          )}
          {selectedTab != 0 && !tabHasNotifications && !isNotificationsLoading && (
            <ListItem className={cx(styles.textItem, styles.emptyItem)}>
              <Typography color="text.secondary" align="center" variant="body1">
                Nothing to see here yet.
              </Typography>
            </ListItem>
          )}
          {lastSevenDaysNotifications.length > 0 && (
            <Typography
              color="text.secondary"
              variant="body2"
              className={styles.title}
            >
              Last 7 days
            </Typography>
          )}

          {lastSevenDaysNotifications?.map((item) => (
            <MobileNotificationItem
              key={item?._id}
              notification={item}
              onClick={onNotificationItemClick}
            />
          ))}

          {restNotifications.length > 0 &&
            lastSevenDaysNotifications.length > 0 && (
              <Typography
                color="text.secondary"
                variant="body2"
                className={styles.title}
              >
                Older
              </Typography>
            )}
          {restNotifications?.map((item) => (
            <MobileNotificationItem
              key={item?._id}
              notification={item}
              onClick={onNotificationItemClick}
            />
          ))}

          {hasNotifications && hasNextPage && (
            <div ref={loaderContainerRef}>
              {isFetchingNextPage && <NotificationItemLoader />}
            </div>
          )}

          {((selectedTab === 0 && hasNotifications) ||
            (selectedTab !== 0 && tabHasNotifications)) &&
            !hasNextPage && (
              <ListItem className={cx(styles.textItem, styles.emptyItem)}>
                <Typography
                  color="text.secondary"
                  align="center"
                  variant="body2"
                >
                  That&apos;s all your notifications.
                </Typography>
              </ListItem>
            )}
        </List>
      </div>
    </MobileModal>
  );
};

export default MobileNotificationPanel;
